import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  TablePagination,
  Chip,
} from '@mui/material';
import { Search as SearchIcon, MailOutline as MailIcon } from '@mui/icons-material';
import { userService, User } from '../services/userService';
import { useNavigate } from 'react-router-dom';
import { getApiCollection, getProp } from '../utils/apiResponse';
import { styles } from '../utils/styles';

export const SharedMailboxes: React.FC = () => {
  const [mailboxes, setMailboxes] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const navigate = useNavigate();

  useEffect(() => {
    loadMailboxes();
  }, []);

  const loadMailboxes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.getUsers({ top: 999 });
      const allUsers = getApiCollection<User>(response, 'loading shared mailboxes');

      // Filter for shared mailboxes
      // Shared mailboxes typically have:
      // - mail property (mailbox enabled)
      // - accountEnabled: false (disabled for sign-in)
      // - no assigned licenses
      // - userType is null or "Member"
      const sharedMailboxes = allUsers.filter((user: any) => {
        const mail = getProp(user, 'mail');
        const accountEnabled = getProp(user, 'accountEnabled');
        const assignedLicenses = getProp(user, 'assignedLicenses') || [];
        const userType = getProp(user, 'userType');

        // Shared mailboxes have mail, are disabled, have no licenses, and are not guests
        return mail && !accountEnabled && assignedLicenses.length === 0 && userType !== 'Guest';
      });

      setMailboxes(sharedMailboxes);
    } catch (err: any) {
      setError(err.message || 'Failed to load shared mailboxes');
    } finally {
      setLoading(false);
    }
  };

  const filteredMailboxes = useMemo(() => {
    if (!searchTerm) return mailboxes;

    const search = searchTerm.toLowerCase();
    return mailboxes.filter((mailbox: any) => {
      const displayName = getProp(mailbox, 'displayName')?.toLowerCase() || '';
      const mail = getProp(mailbox, 'mail')?.toLowerCase() || '';
      const department = getProp(mailbox, 'department')?.toLowerCase() || '';
      return displayName.includes(search) || mail.includes(search) || department.includes(search);
    });
  }, [mailboxes, searchTerm]);

  const paginatedMailboxes = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredMailboxes.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredMailboxes, page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (userId: string) => {
    navigate(`/users/${userId}`);
  };

  if (loading) {
    return (
      <Box sx={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={styles.pageHeader}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MailIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Shared Mailboxes
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={styles.filterSection}>
        <TextField
          placeholder="Search shared mailboxes..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 300 }}
        />
        <Typography variant="body2" color="text.secondary">
          {filteredMailboxes.length} shared mailbox{filteredMailboxes.length !== 1 ? 'es' : ''}
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={styles.tableHead}>
            <TableRow>
              <TableCell sx={styles.tableHeaderCell}>Display Name</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Email Address</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Department</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedMailboxes.map((mailbox: any) => {
              const id = getProp(mailbox, 'id');
              const displayName = getProp(mailbox, 'displayName');
              const mail = getProp(mailbox, 'mail');
              const department = getProp(mailbox, 'department');
              const accountEnabled = getProp(mailbox, 'accountEnabled');

              return (
                <TableRow key={id} hover sx={styles.tableRow} onClick={() => handleRowClick(id)}>
                  <TableCell>{displayName}</TableCell>
                  <TableCell>{mail}</TableCell>
                  <TableCell>{department || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={accountEnabled ? 'Enabled' : 'Disabled'}
                      size="small"
                      color={accountEnabled ? 'success' : 'default'}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
            {paginatedMailboxes.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No shared mailboxes matched the current data set. If you expected results,
                    verify mailbox attributes and API permissions.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredMailboxes.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
};
